import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { LoadingOverlay } from '@/components/ui/LoadingOverlay';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { usePets } from '../hooks/usePets';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../hooks/useSubscription';
import { canAddPet } from '../services/subscriptionService';
import UpgradeModal from './UpgradeModal';

// ✅ NEW: Import dynamic species/breeds functionality
import { useSpeciesAndBreeds } from '../hooks/useSpeciesAndBreeds';
import { BreedAutocomplete } from './BreedAutocomplete';
import { Species, Breed } from '../services/categoryService';

interface AddPetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Opciones de sexo para las mascotas
// Sex options will be dynamically translated using t() function

export default function AddPetModal({ isOpen, onClose }: AddPetModalProps) {
  const { t } = useTranslation();
  const { createPet, isCreating } = usePets();
  const { subscription } = useSubscription();
  
  // ✅ NEW: Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // ✅ NEW: Use dynamic species and breeds
  const {
    species: speciesList,
    breeds,
    isLoadingSpecies,
    isLoadingBreeds,
    selectedSpeciesId,
    setSelectedSpeciesId,
    speciesError,
    breedsError
  } = useSpeciesAndBreeds();
  
  const [name, setName] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const [selectedBreed, setSelectedBreed] = useState<Breed | null>(null);
  const [sex, setSex] = useState('not_specified');
  const [birthDate, setBirthDate] = useState('');
  const [birthDateObj, setBirthDateObj] = useState<Date | null>(null);
  const [weight, setWeight] = useState('');
  const [color, setColor] = useState('');
  const [isMemorial, setIsMemorial] = useState(false);
  const [personalityTraits, setPersonalityTraits] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [dateInputType, setDateInputType] = useState<'calendar' | 'manual'>('manual');
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [creationStep, setCreationStep] = useState<string>('');
  const [creationProgress, setCreationProgress] = useState<string>('');
  
  // New compatibility fields
  const [energyLevel, setEnergyLevel] = useState('');
  const [sizeCategory, setSizeCategory] = useState('');
  const [isNeutered, setIsNeutered] = useState<boolean | null>(null);
  const [socializationLevel, setSocializationLevel] = useState('');
  const [vaccinationStatus, setVaccinationStatus] = useState('');

  // Maximum file size: 50MB
  const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file size (50MB max)
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        toast.error(t('modals.addPet.photoTooLarge', { size: fileSizeMB, max: 50 }));
        return;
      }
      
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const toggleDateInputType = () => {
    setDateInputType(prev => prev === 'calendar' ? 'manual' : 'calendar');
  };
  
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setBirthDateObj(date);
      setBirthDate(format(date, 'yyyy-MM-dd'));
      setCalendarDate(date);
    }
  };

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBirthDate(value);
    if (value) {
      const date = new Date(value + 'T00:00:00');
      if (!isNaN(date.getTime())) {
        setBirthDateObj(date);
        setCalendarDate(date);
      } else {
        setBirthDateObj(null);
      }
    } else {
      setBirthDateObj(null);
    }
  };

  const navigateCalendar = (direction: 'prev' | 'next', unit: 'month' | 'year') => {
    const newDate = new Date(calendarDate);
    if (unit === 'month') {
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
    } else {
      if (direction === 'prev') {
        newDate.setFullYear(newDate.getFullYear() - 1);
      } else {
        newDate.setFullYear(newDate.getFullYear() + 1);
      }
    }
    setCalendarDate(newDate);
  };

  // ✅ NEW: Handle species selection with breed reset
  const handleSpeciesChange = (value: string) => {
    const species = speciesList.find(s => s.id.toString() === value);
    if (species) {
      setSelectedSpecies(species);
      setSelectedSpeciesId(species.id);
      setSelectedBreed(null); // Reset breed when species changes
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !selectedSpecies) {
      toast.error(t('modals.addPet.requiredFields'));
      return;
    }

    // Validate weight if provided
    if (weight && (isNaN(Number(weight)) || Number(weight) <= 0 || Number(weight) > 1500)) {
      toast.error(t('modals.addPet.weightError'));
      return;
    }
    
    // ✅ FASE 3: Check pet limit before creating
    const petLimitCheck = await canAddPet();
    if (!petLimitCheck.canAdd) {
      console.log('🚫 Pet limit reached, showing upgrade modal');
      setShowUpgradeModal(true);
      return;
    }
    
    const traits = personalityTraits
      .split(',')
      .map(trait => trait.trim())
      .filter(trait => trait !== '');
    
    // ✅ NEW: Build pet data with both breed_id and breed text for compatibility
    const petData = {
      name: name.trim(),
      species: selectedSpecies.code, // Use species code
      breed: selectedBreed?.name || undefined, // Keep breed text for compatibility
      breed_id: selectedBreed?.id || undefined, // NEW: Include breed_id
      sex: sex as 'male' | 'female' | 'not_specified',
      birth_date: birthDate || undefined,
      weight_kg: weight ? Number(weight) : undefined,
      color: color.trim() || undefined,
      is_memorial: isMemorial,
      personality_traits: traits.length > 0 ? traits : undefined,
      photo: photoFile || undefined,
      privacy_setting: 'public' as const,
      // New compatibility fields
      energy_level: energyLevel || undefined,
      size_category: sizeCategory || undefined,
      is_neutered: isNeutered ?? undefined,
      socialization_level: socializationLevel || undefined,
      vaccination_status: vaccinationStatus || undefined
    };
    
    try {
      // Set initial creation step
      setCreationStep(t('modals.addPet.creatingSteps.starting'));
      setCreationProgress('');
      
      // Update progress for photo upload if photo exists
      if (photoFile) {
        setCreationStep(t('modals.addPet.creatingSteps.uploadingPhoto'));
        setCreationProgress('1 of 3');
      }
      
      // Update progress for pet creation
      setTimeout(() => {
        setCreationStep(t('modals.addPet.creatingSteps.creatingProfile'));
        setCreationProgress(photoFile ? '2 of 3' : '1 of 2');
      }, 500);
      
      // Update progress for finalizing
      setTimeout(() => {
        setCreationStep(t('modals.addPet.creatingSteps.finalizing'));
        setCreationProgress(photoFile ? '3 of 3' : '2 of 2');
      }, 1000);
      
      await createPet(petData);
      onClose();
      resetForm();
    } catch (error: any) {
      // Error handling is already done in the usePets hook
      console.error('Pet creation error:', error);
    } finally {
      setCreationStep('');
      setCreationProgress('');
    }
  };

  const resetForm = () => {
    setName('');
    setSelectedSpecies(null);
    setSelectedBreed(null);
    setSelectedSpeciesId(null);
    setSex('not_specified');
    setBirthDate('');
    setBirthDateObj(null);
    setWeight('');
    setColor('');
    setIsMemorial(false);
    setPersonalityTraits('');
    setPhotoFile(null);
    setPhotoPreview(null);
    setDateInputType('manual');
    setCalendarDate(new Date());
    setCreationStep('');
    setCreationProgress('');
    setEnergyLevel('');
    setSizeCategory('');
    setIsNeutered(null);
    setSocializationLevel('');
    setVaccinationStatus('');
  };

  return (
    <>
      <LoadingOverlay 
        isVisible={isCreating} 
        message={creationStep || t('modals.addPet.creating')}
        progress={creationProgress}
      />
      <Dialog open={isOpen} onOpenChange={isCreating ? undefined : onClose}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('modals.addPet.title')}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col items-center mb-4">
            <Label 
              htmlFor="photo" 
              className="bg-pet-purple text-white px-3 py-1 rounded-md cursor-pointer"
            >
              {photoFile ? t('modals.addPet.changePhoto') : t('modals.addPet.uploadPhoto')}
            </Label>
            <Input 
              id="photo"
              type="file" 
              accept="image/*"
              className="hidden" 
              onChange={handlePhotoChange}
            />
            {photoPreview && (
              <img 
                src={photoPreview} 
                alt="Pet Preview" 
                className="mt-2 rounded-full w-24 h-24 object-cover" 
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t('modals.addPet.petName')}*</Label>
            <Input
              id="name"
              placeholder={t('modals.addPet.petNamePlaceholder') || "Max, Luna, Charlie..."}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="species">{t('modals.addPet.species')}*</Label>
            {isLoadingSpecies ? (
              <Skeleton className="h-10 w-full" />
            ) : speciesError ? (
              <div className="text-sm text-red-500">Error loading species: {speciesError}</div>
            ) : (
              <Select 
                value={selectedSpecies?.id.toString() || ''} 
                onValueChange={handleSpeciesChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('modals.addPet.selectSpecies')} />
                </SelectTrigger>
                <SelectContent>
                  {speciesList.map((species) => (
                    <SelectItem key={species.id} value={species.id.toString()}>
                      {species.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          <BreedAutocomplete
            breeds={breeds}
            selectedBreed={selectedBreed}
            onBreedSelect={setSelectedBreed}
            disabled={!selectedSpecies || isLoadingBreeds}
            loading={isLoadingBreeds}
            placeholder={
              !selectedSpecies 
                ? t('modals.addPet.firstSelectSpecies')
                : isLoadingBreeds 
                  ? t('modals.addPet.loadingBreeds')
                  : t('modals.addPet.selectBreed')
            }
          />
          
          {breedsError && (
            <div className="text-sm text-red-500">Error loading breeds: {breedsError}</div>
          )}

          <div className="space-y-2">
            <Label htmlFor="sex">{t('modals.addPet.sex')}</Label>
            <Select value={sex} onValueChange={setSex}>
              <SelectTrigger>
                <SelectValue placeholder={t('modals.addPet.selectSex')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">{t('modals.sexOptions.male')}</SelectItem>
                <SelectItem value="female">{t('modals.sexOptions.female')}</SelectItem>
                <SelectItem value="not_specified">{t('modals.sexOptions.notSpecified')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="weight">{t('modals.addPet.weight')}</Label>
            <Input
              id="weight"
              type="number"
              step="0.01"
              min="0.01"
              max="1500"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder={t('modals.addPet.weightPlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="color">{t('modals.addPet.color')}</Label>
            <Input
              id="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder={t("modals.addPet.colorPlaceholder")}
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="birth_date">{t('modals.addPet.birthDate')}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={toggleDateInputType}
                className="h-6 text-xs text-muted-foreground"
              >
                {dateInputType === 'calendar' ? t('modals.addPet.enterManually') : t('modals.addPet.useCalendar')}
              </Button>
            </div>
            
            {dateInputType === 'calendar' ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !birthDateObj && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {birthDateObj ? format(birthDateObj, "PPP") : t('modals.addPet.selectDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="p-3">
                    {/* Year/Month Navigation */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => navigateCalendar('prev', 'year')}
                        >
                          <ChevronLeft className="h-3 w-3" />
                          <ChevronLeft className="h-3 w-3 -ml-1" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => navigateCalendar('prev', 'month')}
                        >
                          <ChevronLeft className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="text-sm font-medium">
                        {format(calendarDate, "MMMM yyyy")}
                      </div>
                      <div className="flex items-center space-x-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => navigateCalendar('next', 'month')}
                        >
                          <ChevronRight className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => navigateCalendar('next', 'year')}
                        >
                          <ChevronRight className="h-3 w-3" />
                          <ChevronRight className="h-3 w-3 -ml-1" />
                        </Button>
                      </div>
                    </div>
                    <Calendar
                      mode="single"
                      selected={birthDateObj || undefined}
                      onSelect={handleDateSelect}
                      month={calendarDate}
                      onMonthChange={setCalendarDate}
                      disabled={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                      initialFocus
                      className={cn("pointer-events-auto")}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <Input
                id="birth_date"
                type="date"
                value={birthDate}
                onChange={handleDateInput}
                max={format(new Date(), "yyyy-MM-dd")}
                min="1900-01-01"
              />
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="personality_traits">{t('modals.addPet.personalityTraits')}</Label>
            <Textarea
              id="personality_traits"
              value={personalityTraits}
              onChange={(e) => setPersonalityTraits(e.target.value)}
              placeholder={t('modals.addPet.personalityPlaceholder')}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="energy_level">{t('modals.addPet.energyLevel')}</Label>
            <Select value={energyLevel} onValueChange={setEnergyLevel}>
              <SelectTrigger>
                <SelectValue placeholder={t('modals.addPet.selectEnergyLevel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="very_low">{t('modals.energyLevels.veryLow')}</SelectItem>
                <SelectItem value="low">{t('modals.energyLevels.low')}</SelectItem>
                <SelectItem value="moderate">{t('modals.energyLevels.moderate')}</SelectItem>
                <SelectItem value="high">{t('modals.energyLevels.high')}</SelectItem>
                <SelectItem value="very_high">{t('modals.energyLevels.veryHigh')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="size_category">{t('modals.addPet.sizeCategory')}</Label>
            <Select value={sizeCategory} onValueChange={setSizeCategory}>
              <SelectTrigger>
                <SelectValue placeholder={t('modals.addPet.selectSizeCategory')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="toy">{t('modals.sizeCategories.toy')}</SelectItem>
                <SelectItem value="small">{t('modals.sizeCategories.small')}</SelectItem>
                <SelectItem value="medium">{t('modals.sizeCategories.medium')}</SelectItem>
                <SelectItem value="large">{t('modals.sizeCategories.large')}</SelectItem>
                <SelectItem value="giant">{t('modals.sizeCategories.giant')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="socialization_level">{t('modals.addPet.socializationLevel')}</Label>
            <Select value={socializationLevel} onValueChange={setSocializationLevel}>
              <SelectTrigger>
                <SelectValue placeholder={t('modals.addPet.selectSocializationLevel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shy">{t('modals.socializationLevels.shy')}</SelectItem>
                <SelectItem value="moderate">{t('modals.socializationLevels.moderate')}</SelectItem>
                <SelectItem value="very_social">{t('modals.socializationLevels.verySocial')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vaccination_status">{t('modals.addPet.vaccinationStatus')}</Label>
            <Select value={vaccinationStatus} onValueChange={setVaccinationStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('modals.addPet.selectVaccinationStatus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="up_to_date">{t('modals.vaccinationStatus.upToDate')}</SelectItem>
                <SelectItem value="partial">{t('modals.vaccinationStatus.partial')}</SelectItem>
                <SelectItem value="unknown">{t('modals.vaccinationStatus.unknown')}</SelectItem>
                <SelectItem value="none">{t('modals.vaccinationStatus.none')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2 py-2">
            <Switch
              id="is_neutered"
              checked={isNeutered === true}
              onCheckedChange={(checked) => setIsNeutered(checked)}
            />
            <Label htmlFor="is_neutered">{t('modals.addPet.neutered')}</Label>
          </div>
          
          <div className="flex items-center space-x-2 py-2">
            <Switch
              id="is_memorial"
              checked={isMemorial}
              onCheckedChange={setIsMemorial}
            />
            <Label htmlFor="is_memorial">{t('modals.addPet.memorial')}</Label>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isCreating}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-pet-purple hover:bg-pet-purple/90"
              disabled={isCreating}
            >
              {isCreating ? t('modals.addPet.creating') : t('modals.addPet.addPet')}
            </Button>
          </div>
        </form>
        </DialogContent>
      </Dialog>
      
      {/* ✅ FASE 3: Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason="pets"
        currentTier={subscription?.data?.subscription_tier || 'free'}
      />
    </>
  );
}